/* LEGEND — News rail widget. Ported from legend/news.jsx (NewsList): latest
   headline per ticker — tag · ticker · age · title · float/mkt-cap chips.
   Click a row to chart that symbol (chart-only, never a board add).

   PERF (the quote-flood lessons, applied):
   - This component OWNS its seed + `news:feed` subscription and state — wire
     frames re-render only this widget's subtree, never Terminal's tree (the
     HeatStrip / Watchlist-heat rule). No widget mounted → no subscription →
     the channel costs the client nothing.
   - Frames are COALESCED: bursts (premarket PR waves tag dozens of symbols a
     minute) buffer in a ref and flush into ONE setState per 250ms window, so
     worst-case render rate is 4/s regardless of wire rate.
   - One row per symbol, list capped at MAX_ROWS — bounded DOM, bounded sort.
   - Ages tick on a single 30s interval (shared with the row map render). */
import React from 'react';
import { fmtSharesShort, fmtMoneyShort } from '../lib/format';
import { getNewsFeedSeed, subscribeNewsFeed } from '../data/source';
import { newsTag, ageOf } from './StockInfoCard';

const { useState: useStateN, useRef: useRefN, useEffect: useEffectN } = React;
const h = React.createElement;

const MAX_ROWS = 60;
const FLUSH_MS = 250;

const NW_TONE: Record<string, string> = {
  PR: 'var(--up)', SEC: 'var(--warn)', HALT: 'var(--down)', WIRE: 'var(--accent)',
};

// Prototype float tiers (data.js floatTier): the chip runs hot under 10M.
function floatHot(f: number | null | undefined): boolean {
  return typeof f === 'number' && f > 0 && f < 10e6;
}

const pubMs = (r: any): number => {
  const t = new Date(r.publishedAt).getTime();
  return Number.isFinite(t) ? t : 0;
};

export function NewsRail({ activeSymbol, onSelect }: { activeSymbol: string; onSelect: (row: any) => void }) {
  const [rows, setRows] = useStateN<any[]>([]);
  const [nowMs, setNowMs] = useStateN(Date.now());
  const bufRef = useRefN<any[]>([]);
  const flushTimerRef = useRefN<any>(null);

  useEffectN(() => {
    let alive = true;
    getNewsFeedSeed(MAX_ROWS).then((seed: any[]) => {
      if (!alive || !seed.length) return;
      // Seed merges UNDER anything live frames already delivered.
      setRows((prev) => {
        const bySym = new Map<string, any>(seed.map((r) => [r.symbol, r]));
        for (const r of prev) bySym.set(r.symbol, r);
        return [...bySym.values()].sort((a, b) => pubMs(b) - pubMs(a)).slice(0, MAX_ROWS);
      });
    });
    const off = subscribeNewsFeed((row: any) => {
      bufRef.current.push(row);
      if (flushTimerRef.current) return;
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        const buf = bufRef.current;
        bufRef.current = [];
        setRows((prev) => {
          const bySym = new Map<string, any>(prev.map((r) => [r.symbol, r]));
          for (const r of buf) bySym.set(r.symbol, r);
          return [...bySym.values()].sort((a, b) => pubMs(b) - pubMs(a)).slice(0, MAX_ROWS);
        });
      }, FLUSH_MS);
    });
    const ageTimer = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => {
      alive = false;
      off();
      clearInterval(ageTimer);
      if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }
    };
  }, []);

  if (!rows.length) {
    return h('div', { className: 'newslist' },
      h('div', { className: 'nl-empty mono' }, 'news wire — waiting for headlines'));
  }

  return h('div', { className: 'newslist' },
    rows.map((r) => {
      const tag = newsTag(r.source);
      const mktCap = r.marketCap;
      return h('div', {
        key: r.symbol,
        className: 'nl-row' + (r.symbol === activeSymbol ? ' active' : ''),
        onClick: () => onSelect && onSelect(r),
        title: r.headline,
      },
        h('span', { className: 'nw-tag', style: { '--tone': NW_TONE[tag] || 'var(--accent)' } as any }, tag),
        h('div', { className: 'nw-main' },
          h('div', { className: 'nw-top' },
            h('span', { className: 'nw-sym' }, r.symbol),
            h('span', { className: 'nw-age mono' }, ageOf(r.publishedAt, nowMs))),
          h('div', { className: 'nw-title' }, r.headline),
          h('div', { className: 'nl-meta' },
            h('span', { className: 'nl-chip' + (floatHot(r.floatShares) ? ' hot' : '') },
              h('b', null, 'Float'), r.floatShares != null ? fmtSharesShort(r.floatShares) : '—'),
            h('span', { className: 'nl-chip' },
              h('b', null, 'Mkt cap'), mktCap != null ? fmtMoneyShort(mktCap) : '—'))));
    }));
}
